import MaskedTextField from "@/common/components/MaskedTextField"
import SelectController from "@/common/components/SelectController"
import TextField from "@/common/components/TextField"
import { customUnitMask } from "@/common/constants/masks.constant"
import { useEquipmentCategories } from "@/common/hooks/queries/useEquipmentCategories"
import { useEquipmentTiers } from "@/common/hooks/queries/useEquipmentTiers"
import useDebounce from "@/common/hooks/useDebounce"
import { Mapper } from "@/common/services/mapper"
import { EquipmentTierData } from "@/common/types/equipmentTier"
import { useEquipmentModels } from "@/features/equipmentModels/hooks/useEquipmentModels"
import { EquipmentCategoryData } from "@/features/equipmentModels/types/equipmentCategories"
import { EquipmentModelData } from "@/features/equipmentModels/types/equipmentModels"
import { FieldComponent } from "@/features/privateCompanies/components/createEquipmentForm/FieldComponent"
import { options as propertyKindOptions } from "@/features/privateCompanies/constants/propertyKindOptions.constant"
import { unitStrategy, unitTranslation } from "@/features/privateCompanies/constants/unitStrategy.constant"
import { options as usageKindOptions } from "@/features/privateCompanies/constants/usageKindOptions.constant"
import { CompanyEquipmentValidationSchema } from "@/features/privateCompanies/schemas/createEquipment.schema"
import { CompanyEquipmentCategoriesTransformer } from "@/features/privateCompanies/services/companyEquipmentCategoriesTransformer"
import { SearchIcon } from "lucide-react"
import { Control, FieldErrors, UseFormRegister, UseFormSetValue, UseFormWatch } from "react-hook-form"
import { CompanyEquipmentData } from "../../types/companyEquipments.type"

type EquipmentCategoryOption = ReturnType<typeof CompanyEquipmentCategoriesTransformer.optionTransformer>[0]

interface EquipmentDetailsProps {
  equipment?: CompanyEquipmentData
  control: Control<CompanyEquipmentValidationSchema, any>
  register: UseFormRegister<CompanyEquipmentValidationSchema>
  watch: UseFormWatch<CompanyEquipmentValidationSchema>
  setValue: UseFormSetValue<CompanyEquipmentValidationSchema>
  errors: FieldErrors<CompanyEquipmentValidationSchema>
  disabled?: boolean
  updateDisabled?: boolean
}

export const EquipmentDetails = ({
  equipment,
  control,
  register,
  watch,
  setValue,
  errors,
  disabled,
  updateDisabled,
}: EquipmentDetailsProps) => {
  const [searchEquipmentCategory, setSearchEquipmentCategory] = useDebounce('')
  const [searchEquipmentModel, setSearchEquipmentModel] = useDebounce('')
  const [searchEquipmentTier, setSearchEquipmentTier] = useDebounce('')

  const unitForConsumption = watch('unit_for_consumption')
  const equipmentCategory = watch('equipment_category.value')
  const supportsInfocarDataEnrichment = watch('supports_infocar_data_enrichment') ?? false

  const equipmentModelFields = watch('requiredFieldNames') || []

  const { data: equipmentCategories } = useEquipmentCategories<EquipmentCategoryData[]>({
    filters: { search: searchEquipmentCategory },
  })

  const { data: equipmentModels } = useEquipmentModels({
    filters: {
      search: searchEquipmentModel,
      category: equipmentCategory,
    },
    mapper: Mapper.mapToOptions<EquipmentModelData>({ labelFieldName: 'display_name', valueFieldName: 'id' }),
  })

  const { data: equipmentTierOptions } = useEquipmentTiers({
    filters: { search: searchEquipmentTier },
    mapper: Mapper.mapToOptions<EquipmentTierData>({ valueFieldName: 'id', labelFieldName: 'display_name' }),
  })
  
  const handleChangeEquipmentCategory = (selectedOption: EquipmentCategoryOption) => {
    const { requiredFieldNames, supportsInfocarDataEnrichment, ...rest } = selectedOption
    setValue('equipment_category', rest as CompanyEquipmentValidationSchema['equipment_category'])
    setValue('equipment_model', null)
    setValue('official_name', '')
    setValue('serial_number', '')
    setValue('license_plate', '')
    setValue('chassis_number', '')
    setValue('renavam', '')
    setValue('tank_capacity', '')

    setValue('requiredFieldNames', requiredFieldNames)
    setValue('supports_infocar_data_enrichment', supportsInfocarDataEnrichment)
  }

  return (
    <>
      <p>Detalhes do Equipamento</p>
      <SelectController
        name="equipment_category"
        control={control}
        label="Categoria do equipamento"
        placeholder={<SearchIcon />}
        markAsRequired={!updateDisabled}
        onInputChange={(newValue) => setSearchEquipmentCategory(newValue)}
        defaultValue={watch('equipment_category')}
        options={CompanyEquipmentCategoriesTransformer.optionTransformer(equipmentCategories)}
        onChange={(selectedOption) => handleChangeEquipmentCategory(selectedOption as EquipmentCategoryOption)}
        errorMessage={errors.equipment_category?.message}
        isDisabled={disabled || updateDisabled}
      />
      {supportsInfocarDataEnrichment && (
        <p className="text-sm font-light italic -mt-2">Informe a placa ou chassi e após criar o equipamento traremos todos os dados específicos.</p>
      )}
      {!!equipmentCategory && !supportsInfocarDataEnrichment && (
        <SelectController
          name="equipment_model"
          control={control}
          label="Busque por seu modelo por Código Fipe, Marca, Nome e ano"
          placeholder={<SearchIcon />}
          markAsRequired={!updateDisabled}
          onInputChange={(newValue) => setSearchEquipmentModel(newValue)}
          defaultValue={watch('equipment_model')}
          options={equipmentModels}
          errorMessage={errors.equipment_model?.message}
          isDisabled={disabled || updateDisabled}
        />
      )}
      <SelectController
        control={control}
        name="equipment_tier"
        label="Porte"
        options={equipmentTierOptions || []}
        onInputChange={setSearchEquipmentTier}
        isClearable
        errorMessage={errors?.equipment_tier?.message}
        isDisabled={disabled}
      />
      <div className="grid w-full grid-cols-2 gap-x-2 gap-y-4">
        {equipmentModelFields.map((field) => (
          <FieldComponent
            key={field.requiredFieldName}
            fieldName={field.requiredFieldName}
            control={control}
            register={register}
            watch={watch}
            errors={errors}
            disabled={disabled}
            updateDisabled={updateDisabled}
            fullSize={!!equipment?.model?.category?.supports_infocar_data_enrichment && field.requiredFieldName === 'tank_capacity'}
          />
        ))}
      </div>
      {!equipment?.model?.category?.bypass_consumption_usage_validations && unitForConsumption && (
        <MaskedTextField
          label={unitTranslation[unitForConsumption as keyof typeof unitTranslation]}
          mask={customUnitMask(unitStrategy[unitForConsumption as keyof typeof unitStrategy])}
          className="w-full"
          errorMessage={errors.last_usage_value?.message || undefined}
          disabled={true}
          defaultValue={watch('last_usage_value') || '0'}
        />
      )}
      <SelectController
        control={control}
        name="usage_kind"
        label="Tipo de uso"
        markAsRequired
        options={usageKindOptions}
        errorMessage={errors.usage_kind?.message || undefined}
        isDisabled={disabled}
      />
      <div className="flex gap-x-2">
        <SelectController
          control={control}
          name="property_kind"
          label="Tipo de propriedade"
          className="w-full"
          options={propertyKindOptions}
          errorMessage={errors.property_kind?.message || undefined}
          isDisabled={disabled}
        />
        <TextField
          label="Código proprietário"
          maxLength={64}
          className="w-full"
          {...register('property_code')}
          errorMessage={errors.property_code?.message || undefined}
          disabled={disabled}
        />
      </div>
    </>
  )
}
